import React, { useEffect, useState } from 'react';
import { useCustomContext } from '../CustomComponents/CustomComponents';
import { insertLog } from '../Logs/InsertLog';
import UsersDs from '../../DataServices/UsersDs';

const usersDS = new UsersDs();

export const UsersLogic = () => {
  const [users, setUsers] = useState([]);
  const currentUserEmail = localStorage.getItem("email")?.toLowerCase();
  const { showAlert, stopHudRotation, hud, showToast } = useCustomContext();

  const useremail = localStorage.getItem("email");
  const userName = useremail?.split('@')[0];
  const [datasets, setDatasets] = useState([]);

  // Search & Pagination
  const [searchEmail, setSearchEmail] = useState("");
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(25);

  const accessLevelList = [
    { value: "1", name: "Admin" },
    { value: "2", name: "Manager" },
    { value: "3", name: "Employee" }
  ];


  useEffect(() => {
    fetchUsers();
    fetchDatasets();
  }, []);

  useEffect(() => {
    setCurrentPage(1);
  }, [searchEmail, pageSize]);

  const fetchUsers = async () => {
    hud("Please Wait...");
    try {
      const response = await usersDS.WS_CognitoUsers_List({});

      const payload = response.data || response;
      if (typeof payload.body !== "string") {
        throw new Error("Unexpected users response");
      }

      const usersArray = JSON.parse(payload.body);

      const filteredAndSortedUsers = usersArray
        .filter(user => {
          const emailAttr = user.Attributes?.find(a => a.Name === "email");
          return emailAttr && emailAttr.Value?.toLowerCase() !== currentUserEmail;
        })
        .sort(
          (a, b) =>
            new Date(b.UserLastModifiedDate) - new Date(a.UserLastModifiedDate)
        );

      const transformedUsers = filteredAndSortedUsers.map(user => {

        const email =
          user.email ||
          user.Attributes?.find(a => a.Name === "email")?.Value ||
          "—";

        const empInfoAttr = user.Attributes?.find(
          a => a.Name === "custom:empInfo"
        );

        let empInfo = {};

        if (empInfoAttr?.Value) {
          try {
            empInfo = JSON.parse(empInfoAttr.Value);
          } catch {
            empInfo = {};
          }
        }

        return {
          ...user,
          email,
          isEmployeeActive: empInfo.isEmployeeActive ?? true
        };

      });

      setUsers(transformedUsers);

    } catch (error) {
      insertLog(
        `${userName} failed to fetch users`,
        0,
        useremail,
        hud,
        stopHudRotation,
        showAlert,
        '',
        'GET',
        'WS_CognitoUsers_List',
        'FAILED',
        error.message
      );

      console.error("Error fetching users:", error);
      showAlert("Failed to fetch users", [
        { label: "OK", onClick: () => { }, color: "var(--buttonColor)" }
      ]);
    } finally {
      stopHudRotation();
    }
  };

  const fetchDatasets = async () => {
    try {
      const response = await usersDS.AP_Dataset_List_GET({});
      const payload = response?.data ?? response;

      let datasetArray = [];

      if (typeof payload?.body === "string") {
        const parsed = JSON.parse(payload.body);

        if (Array.isArray(parsed.records)) {
          datasetArray = parsed.records;
        } else {
          throw new Error("records not found in dataset response");
        }
      } else {
        throw new Error("Invalid dataset response format");
      }

      setDatasets(datasetArray);
    } catch (error) {
      insertLog(
        `${userName} failed to fetch datasets`,
        0,
        useremail,
        hud,
        stopHudRotation,
        showAlert, 
        '',
        'GET',
        'AP_Dataset_List_GET',
        'FAILED',
        error.message
      );

      console.error("Error fetching datasets:", error);
      showAlert("Failed to fetch datasets", [
        { label: "OK", onClick: () => { }, color: "var(--buttonColor)" }
      ]);
    }
  };

  /** --------------------------
   *  CONFIRM USER (via update)
   *  -------------------------- */
  const handleConfirmUser = async (username) => {
    hud("Please wait...");
    try {
      if (!username) {
        showAlert("Invalid user", [{ label: "OK", onClick: () => { }, color: "var(--buttonColor)" }]);
        return;
      }

      const user = users.find((u) => u.Username === username);
      const userEmail = user?.Attributes?.find(a => a.Name === "email")?.Value || username; // ← add this

      const payload = {
        username,
        attributes: [{ Name: "email_verified", Value: "true" }]
      };

      const response = await usersDS.WS_Cognito_UserAttributes_UPDATE(payload);
      const data = response?.data ?? response;
      const parsedBody = typeof data.body === "string" ? JSON.parse(data.body) : data.body;

      if (data.statusCode === 200 || response?.status === 200) {
        showToast({ message: "User confirmed successfully!" });
        insertLog(`${userName} confirmed the user ${userEmail}`, 0, useremail, hud, stopHudRotation, showAlert, username || '', 'UPDATE', 'WS_Cognito_UserAttributes_UPDATE', 'SUCCESS', '');
      } else {
        showAlert("Failed to confirm user", [{ label: "OK", onClick: () => { }, color: "var(--buttonColor)" }]);
        insertLog(
          `${userName} failed to confirm user ${userEmail}`,
          0,
          useremail,
          hud,
          stopHudRotation,
          showAlert,
          username || '',
          'UPDATE',
          'WS_Cognito_UserAttributes_UPDATE',
          'FAILED',
          'Unable to confirm user'
        );
      }
      await fetchUsers();
    } catch (error) {
      const userEmail = users.find((u) => u.Username === username)?.Attributes?.find(a => a.Name === "email")?.Value || username;
      insertLog(`${userName} failed to confirm user ${userEmail}`, 0, useremail, hud, stopHudRotation, showAlert, username || '', 'UPDATE', 'WS_Cognito_UserAttributes_UPDATE', 'FAILED', error.message);
      showAlert("Failed to confirm user", [{ label: "OK", onClick: () => { }, color: "var(--buttonColor)" }]);
    } finally {
      stopHudRotation();
    }
  };

  /** --------------------------
   *  APPROVE USER
   *  -------------------------- */
  const handleApproveUser = async (username) => {
    hud("Please Wait...");
    try {
      if (!username) {
        showAlert("Invalid user", [{ label: "OK", onClick: () => { }, color: "var(--buttonColor)" }]);
        return;
      }

      const user = users.find((u) => u.Username === username);
      if (!user) throw new Error("User not found");
      const userEmail = user?.Attributes?.find(a => a.Name === "email")?.Value || username; // ← add this

      const empInfoAttr = user.Attributes?.find(a => a.Name === "custom:empInfo");
      let empInfo = {};
      if (empInfoAttr?.Value) {
        try { empInfo = JSON.parse(empInfoAttr.Value); } catch { empInfo = {}; }
      }
      empInfo.isEmployeeApproved = true;

      const payload = { username, attributes: [{ Name: "custom:empInfo", Value: JSON.stringify(empInfo) }] };
      const response = await usersDS.WS_Cognito_UserAttributes_UPDATE(payload);
      const data = response?.data ?? response;
      const parsedBody = typeof data.body === "string" ? JSON.parse(data.body) : data.body;

      if (data.statusCode === 200 || response?.status === 200) {
        showToast({ message: "User approved successfully!" });
        insertLog(`${userName} approved the user ${userEmail}`, 0, useremail, hud, stopHudRotation, showAlert, username || '', 'UPDATE', 'WS_Cognito_UserAttributes_UPDATE', 'SUCCESS', '');
      } else {
        showAlert("Failed to approve user", [{ label: "OK", onClick: () => { }, color: "var(--buttonColor)" }]);
        insertLog(
          `${userName} failed to approve user ${userEmail}`,
          0,
          useremail,
          hud,
          stopHudRotation,
          showAlert,
          username || '',
          'UPDATE',
          'WS_Cognito_UserAttributes_UPDATE',
          'FAILED',
          'Unable to approve user'
        );
      }
      await fetchUsers();
    } catch (error) {
      const userEmail = users.find((u) => u.Username === username)?.Attributes?.find(a => a.Name === "email")?.Value || username;
      insertLog(`${userName} failed to approve user ${userEmail}`, 0, useremail, hud, stopHudRotation, showAlert, username || '', 'UPDATE', 'WS_Cognito_UserAttributes_UPDATE', 'FAILED', error.message);
      showAlert("Failed to approve user", [{ label: "OK", onClick: () => { }, color: "var(--buttonColor)" }]);
    } finally {
      stopHudRotation();
    }
  };
  /** --------------------------
   *  ACTIVATE / DISABLE USER
   *  -------------------------- */
  const handleToggleEmployeeActive = async (username, shouldActivate) => {
    hud("Please Wait...");
    try {
      if (!username) {
        showAlert("Invalid user", [{ label: "OK", onClick: () => { }, color: "var(--buttonColor)" }]);
        return;
      }

      const user = users.find((u) => u.Username === username);
      if (!user) throw new Error("User not found");
      const userEmail = user?.Attributes?.find(a => a.Name === "email")?.Value || username; // ← add this

      const empInfoAttr = user.Attributes?.find(a => a.Name === "custom:empInfo");
      let empInfo = {};
      if (empInfoAttr?.Value) {
        try { empInfo = JSON.parse(empInfoAttr.Value); } catch { empInfo = {}; }
      }
      empInfo.isEmployeeActive = shouldActivate;
      empInfo.isEmployeeApproved = true;

      const payload = { username, attributes: [{ Name: "custom:empInfo", Value: JSON.stringify(empInfo) }] };
      const response = await usersDS.WS_Cognito_UserAttributes_UPDATE(payload);
      const data = response?.data ?? response;

      if (data.statusCode === 200 || response?.status === 200) {
        showToast({ message: `User ${shouldActivate ? "enabled" : "disabled"} successfully!` });
        insertLog(`${userName} ${shouldActivate ? "activated" : "disabled"} user ${userEmail}`, 0, useremail, hud, stopHudRotation, showAlert, username || '', 'UPDATE', 'WS_Cognito_UserAttributes_UPDATE', 'SUCCESS', '');
      } else {
        showAlert("Failed to update user status", [{ label: "OK", onClick: () => { }, color: "var(--buttonColor)" }]);
        insertLog(
          `${userName} failed to update status for user ${userEmail}`,
          0,
          useremail,
          hud,
          stopHudRotation,
          showAlert,
          username || '',
          'UPDATE',
          'WS_Cognito_UserAttributes_UPDATE',
          'FAILED',
          'Unable to update user status'
        );
      }
      await fetchUsers();
    } catch (error) {
      const userEmail = users.find((u) => u.Username === username)?.Attributes?.find(a => a.Name === "email")?.Value || username;
      insertLog(`${userName} failed to update status for user ${userEmail}`, 0, useremail, hud, stopHudRotation, showAlert, username || '', 'UPDATE', 'WS_Cognito_UserAttributes_UPDATE', 'FAILED', error.message);
      showAlert("Failed to update user status", [{ label: "OK", onClick: () => { }, color: "var(--buttonColor)" }]);
    } finally {
      stopHudRotation();
    }
  };

  /** --------------------------
   *  UPDATE ACCESS LEVEL
   *  -------------------------- */
  const handleAccessLevelChange = async (username, newLevel) => {
    hud("Please Wait...");
    try {
      if (!username) return;

      const user = users.find((u) => u.Username === username);
      if (!user) return;
      const userEmail = user?.Attributes?.find(a => a.Name === "email")?.Value || username; // ← add this

      const empInfoAttr = user.Attributes?.find(a => a.Name === "custom:empInfo");
      let empInfo = {};
      if (empInfoAttr?.Value) {
        try { empInfo = JSON.parse(empInfoAttr.Value); } catch { empInfo = {}; }
      }
      empInfo.accessLevel = parseInt(newLevel, 10);

      const payload = { username, attributes: [{ Name: "custom:empInfo", Value: JSON.stringify(empInfo) }] };
      const response = await usersDS.WS_Cognito_UserAttributes_UPDATE(payload);
      const data = response?.data ?? response;
      const parsedBody = typeof data.body === "string" ? JSON.parse(data.body) : data.body;
      const accessLabel = accessLevelList.find(a => a.value === newLevel)?.name || newLevel;

      if (data.statusCode === 200 || response?.status === 200) {
        showToast({ message: `Access level updated to "${accessLabel}"` });
        insertLog(`${userName} changed access level of user ${userEmail} to "${accessLabel}"`, 0, useremail, hud, stopHudRotation, showAlert, username || '', 'UPDATE', 'WS_Cognito_UserAttributes_UPDATE', 'SUCCESS', '');
      } else {
        showAlert("Failed to update access level", [{ label: "OK", onClick: () => { }, color: "var(--buttonColor)" }]);
        insertLog(
          `${userName} failed to change access level of user ${userEmail}`,
          0,
          useremail,
          hud,
          stopHudRotation,
          showAlert,
          username || '',
          'UPDATE',
          'WS_Cognito_UserAttributes_UPDATE',
          'FAILED',
          'Unable to update access level'
        );
      }
      await fetchUsers();
    } catch (error) {
      const userEmail = users.find((u) => u.Username === username)?.Attributes?.find(a => a.Name === "email")?.Value || username;
      insertLog(`${userName} failed to change access level of user ${userEmail}`, 0, useremail, hud, stopHudRotation, showAlert, username || '', 'UPDATE', 'WS_Cognito_UserAttributes_UPDATE', 'FAILED', error.message);
      showAlert("Failed to update access level", [{ label: "OK", onClick: () => { }, color: "var(--buttonColor)" }]);
    } finally {
      stopHudRotation();
    }
  };
  // const handleProjectAccessSave = (username, accessArray) => {
  //   saveProjectAccess(
  //     username,
  //     accessArray,
  //     users,
  //     showToast,
  //     fetchUsers,
  //     hud,
  //     stopHudRotation,
  //     showAlert
  //   );
  // };

  const filteredUsers = users.filter(user => {
    const email =
      user.email ||
      user.Attributes?.find(a => a.Name === 'email')?.Value ||
      "";

    return email.toLowerCase().includes(searchEmail.toLowerCase());
  });

  const totalRecords = filteredUsers.length;

  const startEntry =
    totalRecords === 0 ? 0 : (currentPage - 1) * pageSize + 1;

  const endEntry = Math.min(currentPage * pageSize, totalRecords);

  const totalPages = Math.ceil(totalRecords / pageSize);

  useEffect(() => {
    if (currentPage > totalPages && totalPages > 0) {
      setCurrentPage(1);
    }
  }, [totalPages]);

  const paginatedUsers =
    totalRecords === 0
      ? []
      : filteredUsers.slice(
        (currentPage - 1) * pageSize,
        currentPage * pageSize
      );
  /* ===============================
     CHANGE PASSWORD LOGIC
  ================================ */

  const [showPasswordModal, setShowPasswordModal] = useState(false);
  const [passwordUser, setPasswordUser] = useState(null);

  const [passwordForm, setPasswordForm] = useState({
    newPassword: "",
    confirmPassword: ""
  });

  const [passwordErrors, setPasswordErrors] = useState({
    newPassword: "",
    confirmPassword: ""
  });

  const [showNewPassword, setShowNewPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);

  // Regex rules
  const passwordRegex =
    /^(?=.*[a-z])(?=.*[A-Z])(?=.*\d)(?=.*[\^$*.\[\]{}()?"!@#%&\/\\,><':;|_~`+= -])[A-Za-z\d\^$*.\[\]{}()?"!@#%&\/\\,><':;|_~`+= -]{8,}$/;

  /* OPEN MODAL */
  const openChangePasswordModal = (user) => {
    setPasswordUser(user);
    setPasswordForm({
      newPassword: "",
      confirmPassword: ""
    });
    setPasswordErrors({
      newPassword: "",
      confirmPassword: ""
    });
    setShowNewPassword(false);
    setShowConfirmPassword(false);
    setShowPasswordModal(true);
  };

  /* CLOSE MODAL */
  const closeChangePasswordModal = () => {
    setShowPasswordModal(false);
  };

  /* SAVE PASSWORD */
  const handleSavePassword = async () => {
    const { newPassword, confirmPassword } = passwordForm;

    let errors = {
      newPassword: "",
      confirmPassword: ""
    };

    if (!newPassword) {
      errors.newPassword = "New password is required";
    } else if (newPassword !== newPassword.trim()) {
      errors.newPassword =
        "Password cannot start or end with a space";
    } else if (/\s{2,}/.test(newPassword)) {
      errors.newPassword =
        "Password cannot contain multiple consecutive spaces";
    } else if (!passwordRegex.test(newPassword)) {
      errors.newPassword =
        "Password must be at least 8 characters and include uppercase, lowercase, number, and special character";
    }

    if (!confirmPassword) {
      errors.confirmPassword = "Confirm password is required";
    } else if (newPassword !== confirmPassword) {
      errors.confirmPassword = "Passwords do not match";
    }

    setPasswordErrors(errors);
    if (Object.values(errors).some(Boolean)) return;

    const Password = newPassword.trim();
    const userEmail =
      passwordUser?.email ||
      passwordUser?.Attributes?.find(a => a.Name === "email")?.Value ||
      passwordUser?.Username;

    try {
      hud("Changing password...");

      const sub = passwordUser?.Attributes?.find(a => a.Name === "sub")?.Value;
      if (!sub) throw new Error("User sub not found");

      const payload = {
        sub,
        newPassword: Password,
        updated_by_email_id: useremail
      };

      const response = await usersDS.WS_ChangePassword_ByUser(payload);
      const data = response?.data ?? response;
      const parsedBody = typeof data.body === "string" ? JSON.parse(data.body) : data.body;

      if (data.statusCode === 200 || response?.status === 200) {
        showToast({ message: "Password changed successfully!" });
        insertLog(
          `${userName} changed password for user ${userEmail}`,
          0, useremail, hud, stopHudRotation, showAlert,
          passwordUser?.Username || '', 'UPDATE', 'WS_ChangePassword_ByUser', 'SUCCESS', ''
        );
      } else {
        console.log("WS_ChangePassword_ByUser Error :", parsedBody?.message || parsedBody?.error);
        showAlert("Failed to change password", [{ label: "OK", onClick: () => { }, color: "var(--buttonColor)" }]);
        insertLog(
          `${userName} failed to change password for user ${userEmail}`,
          0,
          useremail,
          hud,
          stopHudRotation,
          showAlert,
          passwordUser?.Username || '',
          'UPDATE',
          'WS_ChangePassword_ByUser',
          'FAILED',
          'Unable to change password'
        );
      }

      setShowPasswordModal(false);
    } catch (error) {
      insertLog(
        `${userName} failed to change password for user ${userEmail}`,
        0, useremail, hud, stopHudRotation, showAlert,
        passwordUser?.Username || '', 'UPDATE', 'WS_ChangePassword_ByUser', 'FAILED', error.message
      );
      console.error("Change password failed:", error);
      showAlert("Failed to change password", [{ label: "OK", onClick: () => { }, color: "var(--buttonColor)" }]);
    } finally {
      stopHudRotation();
    }
  };

  return {
    users,
    datasets,
    paginatedUsers,
    totalPages,
    totalRecords,
    startEntry,
    endEntry,
    pageSize,
    setPageSize,
    searchEmail,
    setSearchEmail,
    currentPage,
    setCurrentPage,
    accessLevelList,
    handleConfirmUser,
    handleApproveUser,
    handleToggleEmployeeActive,
    handleAccessLevelChange,
    // handleProjectAccessSave,
    fetchUsers,
    // handleUserSave
    /*change Password*/
    showPasswordModal,
    passwordForm,
    passwordErrors,
    showNewPassword,
    showConfirmPassword,
    setPasswordForm,
    setPasswordErrors,
    setShowNewPassword,
    setShowConfirmPassword,
    openChangePasswordModal,
    closeChangePasswordModal,
    handleSavePassword

  };
};
